/**
 * Instellingen: wie je bent, waarmee de kaart opengaat, en de meldingen over de
 * Funda-ronde. Het bijstellen van de kaart zelf hoort in het weergavepaneel,
 * naast de kaart, en niet hier.
 */
import { formatDatumTijd } from '@mamimap/shared';
import type { ReactNode } from 'react';
import { useUitloggen } from '@/lib/api/queries';
import type { Sessie } from '@/lib/api/schemas';
import {
  KAARTSTIJLEN,
  type Kaartstijl,
  type Lagen,
  useVoorkeuren,
  VOORKEUREN_STANDAARD,
} from '@/lib/instellingen';
import { PushInstelling, RondeInstelling } from '@/mobiel/Meldingen';
import { LAAG_LABELS } from '@/screens/map/ballon';
import { KAARTSTIJL_LABELS } from '@/screens/map/kaartstijlen';

export function InstellingenScherm({ sessie }: { sessie: Sessie }) {
  const uitloggen = useUitloggen();
  const [voorkeuren, zetVoorkeuren] = useVoorkeuren();
  const gebruiker = sessie.gebruiker;

  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-paper">
      <div className="w-full max-w-[640px] px-4 py-5 sm:px-8 sm:py-8">
        <h1 className="kop text-[20px]">Instellingen</h1>
        <div className="mt-3 mb-6 h-px bg-line-strong" />

        <Sectie titel="Account">
          <Regel naam="Naam" waarde={gebruiker?.naam ?? '–'} />
          <Regel naam="E-mailadres" waarde={gebruiker?.email ?? '–'} />
          <Regel
            naam="Sessie verloopt"
            waarde={sessie.verlooptOp === null ? '–' : formatDatumTijd(sessie.verlooptOp)}
          />
          <div className="mt-4">
            <button
              type="button"
              className="knop"
              onClick={() => uitloggen.mutate()}
              disabled={uitloggen.isPending}
            >
              {uitloggen.isPending ? 'Bezig met uitloggen' : 'Uitloggen'}
            </button>
          </div>
        </Sectie>

        {/* Meldingen en de ronde gaan over de huizenzoektocht; wie alleen SportCity heeft
            (MA-72), krijgt ze niet en zou er een 403 achter vinden. */}
        {gebruiker?.werkruimtes.includes('huizen') ? (
          <>
            <Sectie titel="Meldingen">
              <PushInstelling />
            </Sectie>

            <Sectie titel="Funda-ronde">
              <RondeInstelling />
            </Sectie>
          </>
        ) : null}

        <Sectie
          titel="Kaart"
          bijschrift="Waarmee de kaart opengaat, en waar 'terug naar standaard' in het weergavepaneel op uitkomt."
        >
          <Veldgroep naam="Standaard kaartstijl">
            <div className="flex flex-wrap gap-1.5">
              {KAARTSTIJLEN.map((stijl) => (
                <button
                  key={stijl}
                  type="button"
                  className="keuzeknop"
                  data-actief={voorkeuren.stijl === stijl}
                  aria-pressed={voorkeuren.stijl === stijl}
                  onClick={() => zetVoorkeuren({ stijl: stijl as Kaartstijl })}
                >
                  {KAARTSTIJL_LABELS[stijl]}
                </button>
              ))}
            </div>
          </Veldgroep>

          <Veldgroep naam="Standaard kaartlagen">
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(LAAG_LABELS) as (keyof Lagen)[]).map((laag) => (
                <button
                  key={laag}
                  type="button"
                  className="keuzeknop"
                  data-actief={voorkeuren.lagen[laag]}
                  aria-pressed={voorkeuren.lagen[laag]}
                  onClick={() =>
                    zetVoorkeuren({
                      lagen: { ...voorkeuren.lagen, [laag]: !voorkeuren.lagen[laag] },
                    })
                  }
                >
                  {LAAG_LABELS[laag]}
                </button>
              ))}
            </div>
          </Veldgroep>

          <div className="mt-4">
            <button
              type="button"
              className="knop-stil"
              onClick={() => zetVoorkeuren(VOORKEUREN_STANDAARD)}
              disabled={
                voorkeuren.stijl === VOORKEUREN_STANDAARD.stijl &&
                voorkeuren.lagen.clustering === VOORKEUREN_STANDAARD.lagen.clustering &&
                voorkeuren.lagen.percelen === VOORKEUREN_STANDAARD.lagen.percelen &&
                voorkeuren.lagen.luchtfoto === VOORKEUREN_STANDAARD.lagen.luchtfoto &&
                voorkeuren.lagen.passanten === VOORKEUREN_STANDAARD.lagen.passanten &&
                voorkeuren.lagen.bestemmingsplan === VOORKEUREN_STANDAARD.lagen.bestemmingsplan
              }
            >
              Terug naar standaard
            </button>
          </div>
        </Sectie>
      </div>
    </div>
  );
}

function Sectie({
  titel,
  bijschrift,
  children,
}: {
  titel: string;
  bijschrift?: string;
  children: ReactNode;
}) {
  return (
    <section className="mb-8">
      <h2 className="opschrift mb-2">{titel}</h2>
      {bijschrift ? <p className="mb-3 text-[12px] text-muted">{bijschrift}</p> : null}
      <div className="paneel px-4 py-3.5">{children}</div>
    </section>
  );
}

function Regel({ naam, waarde }: { naam: string; waarde: string }) {
  return (
    <div className="flex items-baseline justify-between border-b border-line py-1.5 last:border-b-0 text-[13px]">
      <span className="text-muted">{naam}</span>
      <span className="text-ink">{waarde}</span>
    </div>
  );
}

function Veldgroep({ naam, children }: { naam: string; children: ReactNode }) {
  return (
    <div className="mb-4 last:mb-0">
      <span className="veldnaam">{naam}</span>
      {children}
    </div>
  );
}
